import {
  ItascaOrgTier,
  orgTierToUserTier,
} from '@acretrader/acremaps-permissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/sharp-solid-svg-icons';
import { FC, useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import Typography from '@/components/typography';
import { PRICING_URL } from '@/constants/urls';
import { useAuth } from '@/providers/auth-provider';
import { useModalContext } from '@/providers/ModalProvider';

type Props = {
  label: string;
  value: string | null;
  requiredTier?: ItascaOrgTier | undefined;
  isLoading?: boolean;
  className?: string;
  containerClassName?: string;
};

export const LineItem: FC<Props> = ({
  label,
  value,
  requiredTier = undefined,
  isLoading = false,
  className = undefined,
  containerClassName = undefined,
}) => {
  const { orgGreaterThanOrEqualToTier, isAnonymous } = useAuth();
  const { openAuthModal } = useModalContext();

  const goToPricingPage = (e) => {
    e.stopPropagation();
    window.open(PRICING_URL, '_blank');
  };

  const ctaOnClick = useCallback(
    (e) => {
      if (isAnonymous && requiredTier === ItascaOrgTier.free) {
        e.stopPropagation();
        return openAuthModal('signup');
      }
      return goToPricingPage(e);
    },
    [isAnonymous, requiredTier, openAuthModal]
  );

  const ctaText = useMemo(() => {
    if (isAnonymous && requiredTier === ItascaOrgTier.free) {
      return 'Sign up to view';
    }
    return `Compare Plans`;
  }, [isAnonymous, requiredTier]);

  if (
    !orgGreaterThanOrEqualToTier(orgTierToUserTier(requiredTier)) &&
    requiredTier !== undefined
  ) {
    return (
      <div className="mb-1 flex">
        <Typography
          className="mr-2 whitespace-nowrap capitalize leading-[32px]"
          variant="body2"
        >
          {label}
        </Typography>
        <Button
          className="text-green-50"
          size={ButtonSize.SMALL}
          variant={ButtonVariant.PRIMARY_LIGHT}
          onClick={ctaOnClick}
        >
          {ctaText}
          <FontAwesomeIcon className="ml-2" icon={faArrowRight} />
        </Button>
      </div>
    );
  }

  return (
    <div className={twMerge('mb-1 flex', containerClassName)}>
      <Typography
        className="mr-2 whitespace-nowrap capitalize leading-5"
        variant="body2"
      >
        {label}
      </Typography>
      {isLoading ? (
        <span className="max-h-5">
          <Skeleton className="h-[20px] w-[100px]" />
        </span>
      ) : (
        <Typography
          className={twMerge('capitalize leading-5', className)}
          variant="h6"
        >
          {value}
        </Typography>
      )}
    </div>
  );
};
